/**
 * Options for configuring the waitForElement function
 */
export interface WaitForElementOptions {
    /**
     * Timeout in milliseconds after which the promise will reject.
     * @default 10000 (10 seconds)
     */
    timeout?: number;
}

/**
 * Waits for an element matching the given selector to appear in the DOM and executes the callback when found.
 * @param selector - CSS selector for the element to wait for
 * @param callback - Function to execute when the element is found
 */
export function waitForElement(selector: string | HTMLElement, callback: () => void): void;

/**
 * Waits for an element matching the given selector to appear in the DOM.
 * @param selector - CSS selector for the element to wait for
 * @returns Promise that resolves with the found element or rejects on timeout
 */
export function waitForElement(selector: string | HTMLElement): Promise<Element>;

/**
 * Waits for an element matching the given selector to appear in the DOM with custom options.
 * @param selector - CSS selector or HTMLElement to wait for
 * @param options - Options for configuring timeout
 * @returns Promise that resolves with the found element or rejects on timeout
 */
export function waitForElement(selector: string | HTMLElement, options: WaitForElementOptions): Promise<Element>;

/**
 * Implementation of waitForElement that supports both callback and Promise patterns.
 * @param selector - CSS selector or HTMLElement to wait for
 * @param callbackOrOptions - Optional callback function or options object
 */
export function waitForElement(selector: string | HTMLElement, callbackOrOptions?: (() => void) | WaitForElementOptions): void | Promise<Element> {
    // If callbackOrOptions is a function, use the callback pattern
    if (typeof callbackOrOptions === 'function') {
        // Check if the element already exists
        let foundElement: Element | null = null;

        if (selector instanceof HTMLElement) {
            foundElement = selector;
        } else {
            foundElement = document.querySelector(selector);
        }

        if (foundElement) {
            callbackOrOptions();
            return;
        }

        // Create an observer instance linked to the callback function
        const observer = new MutationObserver((_mutationsList: MutationRecord[], observer: MutationObserver) => {
            // Check if the element is in the DOM
            let foundElement: Element | null = null;
            if (selector instanceof HTMLElement) {
                foundElement = selector;
            } else if (typeof selector === 'string') {
                foundElement = document.querySelector(selector);
            }
            if (foundElement) {
                // Once the element is found, disconnect the observer
                observer.disconnect();
                callbackOrOptions();
            }
        });

        // Options for the observer (which mutations to observe)
        const config: MutationObserverInit = {
            childList: true, // Observe direct children
            subtree: true, // Observe all descendants
        };

        // Start observing the document with the configured parameters
        observer.observe(document, config);
        return;
    }

    // If we get here, we're using the Promise pattern
    const options: WaitForElementOptions = (typeof callbackOrOptions === 'object')
        ? callbackOrOptions
        : {};

    // Set default timeout
    const timeout = options.timeout || 10000;

    return new Promise<Element>((resolve, reject) => {
        let timeoutId: number | undefined;

        // First check if the element already exists
        let element: Element | null = null;

        if (selector instanceof HTMLElement) {
            element = selector;
        } else if (typeof selector === 'string') {
            element = document.querySelector(selector);
        }

        if (element instanceof Element) {
            resolve(element);
            return;
        }

        // Set timeout to reject the promise if element isn't found
        timeoutId = window.setTimeout(() => {
            if (observer) {
                observer.disconnect();
            }
            reject(new Error(`Element ${selector instanceof HTMLElement ? 'HTMLElement' : selector} not found within ${timeout}ms`));
        }, timeout);

        // Create an observer instance linked to the promise resolver
        const observer = new MutationObserver((_mutationsList: MutationRecord[], obs: MutationObserver) => {
            // Check if the element is in the DOM
            let foundElement: Element | null = null;

            if (selector instanceof HTMLElement) {
                foundElement = selector;
            } else if (typeof selector === 'string') {
                foundElement = document.querySelector(selector);
            }

            if (foundElement instanceof Element) {
                // Once the element is found, disconnect the observer
                obs.disconnect();

                // Clear the timeout since we found the element
                if (timeoutId !== undefined) {
                    window.clearTimeout(timeoutId);
                }

                resolve(foundElement);
            }
        });

        // Options for the observer (which mutations to observe)
        const config: MutationObserverInit = {
            childList: true, // Observe direct children
            subtree: true, // Observe all descendants
        };

        // Start observing the document with the configured parameters
        observer.observe(document, config);
    });
}

// Backward compatibility: Export waitForSelector as an alias
export const waitForSelector = waitForElement;